// A screen manager, sized for a game with four screens rather than an app framework.
//
// Screens are overlays over the live paddock, not replacements for it — the canvas keeps rendering
// underneath. That is deliberate: the paddock is the identity of the game, and a title card that
// hides it is a worse first impression than one that sits over it.
//
// Nothing here touches the sim. The one screen that affects the run is the pause, and it does so by
// gating the LOOP — see main.ts.

export class Screens {
  private active: string | null = null

  constructor(private readonly map: Readonly<Record<string, HTMLElement>>) {}

  /** Show one screen, or `null` for none — the player is in the paddock. */
  show(name: string | null): void {
    for (const key of Object.keys(this.map)) {
      const el = this.map[key]
      const on = key === name
      el.hidden = !on
      el.setAttribute('aria-hidden', String(!on))
    }
    this.active = name

    if (name) {
      const screen = this.map[name]
      // Always open at the top. A scrollable screen keeps its previous scroll position otherwise,
      // so a second visit to the help starts wherever the last one ended.
      screen.querySelector('.panel')?.scrollTo({ top: 0 })

      // Move focus into the screen so a keyboard or screen-reader user is not left behind on the
      // canvas, which has nothing to read.
      //
      // `preventScroll` is load-bearing, not defensive: the first focusable on the help screen is
      // the BACK button at the very bottom, so focusing it scrolled the panel past all the content
      // the player had just asked to read.
      const focusable = screen.querySelector<HTMLElement>('button, [href], [tabindex]')
      focusable?.focus({ preventScroll: true })
    }
  }

  get current(): string | null {
    return this.active
  }
}
